import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Plus, Edit, X, Loader2, Package, AlertCircle, CheckCircle, XCircle, RefreshCw, Building2, Layers } from 'lucide-react';
import api from '../api';

const REPORT_LANGUAGE_OPTIONS = ['English', 'Hindi', 'Marathi', 'Gujarati', 'Telugu', 'Bengali', 'Odia'];

const Toast = ({ toasts, removeToast }) => (
  <div className="fixed top-5 right-5 z-[9999] flex flex-col gap-2 pointer-events-none">
    {toasts.map((t) => (
      <div
        key={t.id}
        className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl shadow-xl text-sm font-medium min-w-[280px] max-w-[360px] border backdrop-blur-sm ${
          t.type === 'success' ? 'bg-white border-emerald-200 text-emerald-800' : ''
        } ${t.type === 'error' ? 'bg-white border-rose-200 text-rose-800' : ''} ${t.type === 'info' ? 'bg-white border-blue-200 text-blue-800' : ''}`}
      >
        {t.type === 'success' && <CheckCircle className="h-5 w-5 shrink-0 text-emerald-500 mt-0.5" />}
        {t.type === 'error' && <XCircle className="h-5 w-5 shrink-0 text-rose-500 mt-0.5" />}
        {t.type === 'info' && <Loader2 className="h-5 w-5 shrink-0 text-blue-500 mt-0.5 animate-spin" />}
        <span className="flex-1 leading-snug">{t.message}</span>
        <button onClick={() => removeToast(t.id)} className="opacity-50 hover:opacity-100 transition-opacity">
          <X className="h-4 w-4" />
        </button>
      </div>
    ))}
  </div>
);

const SkeletonRow = ({ cols = 5 }) => (
  <tr className="animate-pulse">
    {[...Array(cols)].map((_, i) => (
      <td key={i} className="px-4 py-4">
        <div className="h-4 bg-slate-100 rounded-full" style={{ width: `${60 + Math.random() * 30}%` }} />
      </td>
    ))}
  </tr>
);

const PackageManagement = () => {
  // Client state
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientError, setClientError] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);

  // Package state
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [packageError, setPackageError] = useState('');

  // Dropdown data
  const [camps, setCamps] = useState([]);
  const [services, setServices] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  // Form modal
  const [showModal, setShowModal] = useState(false);
  const [formMode, setFormMode] = useState('add'); // add | edit
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    camp: '',
    services: [],
    report_language: 'English',
    start_date: '',
    end_date: '',
    technicians: [],
    is_completed: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Toast
  const [toasts, setToasts] = useState([]);
  const toastCounter = React.useRef(0);
  const addToast = useCallback((message, type = 'success') => {
    const id = ++toastCounter.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);
  const removeToast = useCallback((id) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);

  // Fetch clients once
  const fetchClients = useCallback(async () => {
    setLoadingClients(true);
    setClientError('');
    try {
      const res = await api.get('clients/');
      const data = res.data;
      const list = Array.isArray(data) ? data : (data.results || []);
      // Only Client login_type
      const filtered = list.filter((c) => c.login_type === 'Client');
      setClients(filtered);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to load clients';
      setClientError(msg);
      addToast(msg, 'error');
    } finally {
      setLoadingClients(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Fetch meta for dropdowns (camps, services, technicians) once when needed
  const fetchMeta = useCallback(async () => {
    if (camps.length > 0 && services.length > 0 && technicians.length > 0) return;
    setLoadingMeta(true);
    try {
      const results = await Promise.allSettled([
        api.get('campmanager/camps/'),
        api.get('services/'),
        api.get('technician/technicians/'),
      ]);
      if (results[0].status === 'fulfilled') {
        const d = results[0].value.data;
        setCamps(Array.isArray(d) ? d : (d.results || []));
      }
      if (results[1].status === 'fulfilled') {
        const d = results[1].value.data;
        setServices(Array.isArray(d) ? d : (d.results || []));
      }
      if (results[2].status === 'fulfilled') {
        const d = results[2].value.data;
        setTechnicians(Array.isArray(d) ? d : (d.results || []));
      }
    } catch (e) {
      console.error('Meta fetch error', e);
    } finally {
      setLoadingMeta(false);
    }
  }, [camps.length, services.length, technicians.length]);

  // Lookup maps
  const campMap = useMemo(() => {
    const m = new Map();
    camps.forEach((c) => m.set(String(c.id), c));
    return m;
  }, [camps]);
  const serviceMap = useMemo(() => {
    const m = new Map();
    services.forEach((s) => m.set(String(s.id), s));
    return m;
  }, [services]);
  const technicianMap = useMemo(() => {
    const m = new Map();
    technicians.forEach((t) => m.set(String(t.id), t));
    return m;
  }, [technicians]);

  const getCampDisplay = useCallback((campId) => {
    if (!campId) return '-';
    const idStr = String(typeof campId === 'object' ? campId.id || campId : campId);
    const camp = campMap.get(idStr);
    if (camp) return camp.location || camp.name || camp.branch_name || `Camp ${camp.id}`;
    if (typeof campId === 'object' && campId.location) return campId.location;
    return `#${idStr}`;
  }, [campMap]);

  const getServiceNames = useCallback((serviceIds) => {
    if (!serviceIds || serviceIds.length === 0) return '-';
    const ids = Array.isArray(serviceIds) ? serviceIds : [serviceIds];
    return ids.map((sid) => {
      const key = String(typeof sid === 'object' ? sid.id || sid : sid);
      const s = serviceMap.get(key);
      if (s) return s.name;
      if (typeof sid === 'object' && sid.name) return sid.name;
      return `#${key}`;
    }).join(', ');
  }, [serviceMap]);

  const getTechnicianNames = useCallback((techIds) => {
    if (!techIds || techIds.length === 0) return '-';
    const ids = Array.isArray(techIds) ? techIds : [techIds];
    return ids.map((tid) => {
      const key = String(typeof tid === 'object' ? tid.id || tid : tid);
      const t = technicianMap.get(key);
      if (t) return t.name || t.user?.name || `Tech ${key}`;
      if (typeof tid === 'object' && (tid.name || tid.user?.name)) return tid.name || tid.user?.name;
      return `#${key}`;
    }).join(', ');
  }, [technicianMap]);

  // Filter clients by search (local)
  const filteredClients = useMemo(() => {
    if (!clientSearch) return clients;
    const term = clientSearch.toLowerCase();
    return clients.filter((c) =>
      (c.client_id || '').toLowerCase().includes(term) ||
      (c.name || '').toLowerCase().includes(term) ||
      (c.email || '').toLowerCase().includes(term) ||
      (c.contact_number || '').toLowerCase().includes(term)
    );
  }, [clients, clientSearch]);

  // Fetch packages for selected client
  const fetchPackages = useCallback(async (client) => {
    if (!client) return;
    setLoadingPackages(true);
    setPackageError('');
    setPackages([]);
    // Ensure meta loaded for display
    fetchMeta();
    try {
      // Packages API requires auth; api interceptor will attach admin token (Bearer admin_access_token)
      // If you test directly via curl/browser without header you will get {"detail":"Authentication credentials were not provided."}
      // Correct curl: curl -H "Authorization: Bearer <admin_access_token>" "https://cbackend.xraidigital.com/api/rural-health/packages/?client=22"
      const adminToken = localStorage.getItem('admin_access_token');
      if (!adminToken) {
        throw { response: { status: 401, data: { detail: 'Admin token missing. Please login as Admin again.' } } };
      }
      // Explicit header fallback in case interceptor misses relative url normalization
      const authHeader = { Authorization: `Bearer ${adminToken}` };
      let dataList = null;
      let lastErr = null;
      const tryFetch = async (params) => {
        const res = await api.get('rural-health/packages/', { params, headers: authHeader });
        const d = res.data;
        if (Array.isArray(d)) return d;
        if (d.results) return d.results;
        if (d.data) return Array.isArray(d.data) ? d.data : [d.data];
        return [];
      };

      // Candidate 1: client numeric id
      try {
        const list = await tryFetch({ client: client.id });
        // Verify filtering: if list contains only client's packages or empty, we can use it
        // Do client-side verification as safety
        // If backend properly filters, list will be correct. If not, we filter.
        // We determine by checking if any item's client field mismatches
        const hasMismatch = list.some((p) => {
          const pc = p.client;
          if (pc === undefined || pc === null) return false;
          const pcStr = String(typeof pc === 'object' ? pc.id || pc : pc);
          return pcStr !== String(client.id) && pcStr !== String(client.client_id);
        });
        if (hasMismatch) {
          // backend did not filter, apply client-side
          const filtered = list.filter((p) => {
            const pc = p.client;
            if (pc === undefined || pc === null) return false;
            if (typeof pc === 'object') return String(pc.id) === String(client.id) || String(pc.client_id) === String(client.client_id);
            return String(pc) === String(client.id) || String(pc) === String(client.client_id);
          });
          dataList = filtered;
        } else {
          dataList = list;
        }
        // If dataList is null we continue, else success
        if (dataList !== null) {
          // If we got data (even empty), consider success and break
          // But to handle case where client param not supported (backend returns all), we already filtered
          // So we can return
        }
      } catch (e) {
        lastErr = e;
        // Try next param if 400 or empty
        if (e.response?.status === 401 || e.response?.status === 403) throw e;
      }

      if (dataList !== null) {
        setPackages(dataList);
        return;
      }

      // Candidate 2: client_id
      try {
        const list = await tryFetch({ client_id: client.id });
        const hasMismatch = list.some((p) => {
          const pc = p.client;
          if (pc === undefined || pc === null) return false;
          const pcStr = String(typeof pc === 'object' ? pc.id || pc : pc);
          return pcStr !== String(client.id) && pcStr !== String(client.client_id);
        });
        if (hasMismatch) {
          const filtered = list.filter((p) => {
            const pc = p.client;
            if (pc === undefined || pc === null) return false;
            if (typeof pc === 'object') return String(pc.id) === String(client.id) || String(pc.client_id) === String(client.client_id);
            return String(pc) === String(client.id) || String(pc) === String(client.client_id);
          });
          dataList = filtered;
        } else {
          dataList = list;
        }
        if (dataList !== null) {
          setPackages(dataList);
          return;
        }
      } catch (e) {
        lastErr = e;
        if (e.response?.status === 401 || e.response?.status === 403) throw e;
      }

      if (dataList !== null) {
        setPackages(dataList);
        return;
      }

      // Fallback: fetch all and filter client-side
      try {
        const list = await tryFetch({});
        const filtered = list.filter((p) => {
          const pc = p.client;
          if (pc === undefined || pc === null) return false;
          if (typeof pc === 'object') return String(pc.id) === String(client.id) || String(pc.client_id) === String(client.client_id);
          return String(pc) === String(client.id) || String(pc) === String(client.client_id);
        });
        setPackages(filtered);
        return;
      } catch (e) {
        lastErr = e;
        throw e;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to load packages';
      // If 401, show auth message with curl hint
      if (err.response?.status === 401) {
        const authHint = detail.includes('Authentication credentials') ? `${detail} — API requires header: Authorization: Bearer <admin_access_token> (login as Admin first). Example: curl -H "Authorization: Bearer ${localStorage.getItem('admin_access_token') ? localStorage.getItem('admin_access_token').substring(0,12)+'...' : '<token>'}" "https://cbackend.xraidigital.com/api/rural-health/packages/?client=${client.id}"` : detail;
        setPackageError(authHint);
        addToast('Authentication required — please login as Admin', 'error');
        console.error('Rural-health auth failed. Ensure admin_access_token exists and is sent as Bearer:', detail, 'token present:', !!localStorage.getItem('admin_access_token'));
      } else {
        setPackageError(detail);
        addToast(detail, 'error');
      }
    } finally {
      setLoadingPackages(false);
    }
  }, [addToast, fetchMeta]);

  const handleSelectClient = (client) => {
    // toggle if same client clicked again -> collapse
    if (selectedClient?.id === client.id) {
      setSelectedClient(null);
      setPackages([]);
      setPackageError('');
      return;
    }
    setSelectedClient(client);
    fetchPackages(client);
  };

  const handleRefreshPackages = () => {
    if (selectedClient) fetchPackages(selectedClient);
  };

  // Form helpers
  const openAddModal = () => {
    if (!selectedClient) {
      addToast('Please select a client first', 'error');
      return;
    }
    fetchMeta();
    setFormMode('add');
    setEditingPackage(null);
    setFormData({
      name: '',
      price: '',
      camp: '',
      services: [],
      report_language: 'English',
      start_date: '',
      end_date: '',
      technicians: [],
      is_completed: false,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const openEditModal = (pkg) => {
    fetchMeta();
    setFormMode('edit');
    setEditingPackage(pkg);
    // Normalize fields: camp may be object or id, services array, technicians array
    const campVal = pkg.camp ? (typeof pkg.camp === 'object' ? pkg.camp.id : pkg.camp) : '';
    const servicesVal = pkg.services ? pkg.services.map((s) => (typeof s === 'object' ? s.id : s)) : [];
    const techVal = pkg.technicians ? pkg.technicians.map((t) => (typeof t === 'object' ? t.id : t)) : [];
    setFormData({
      name: pkg.name || '',
      price: pkg.price ? String(pkg.price) : '',
      camp: campVal ? String(campVal) : '',
      services: servicesVal.map(String),
      report_language: pkg.report_language || 'English',
      start_date: pkg.start_date ? pkg.start_date.substring(0, 10) : '',
      end_date: pkg.end_date ? pkg.end_date.substring(0, 10) : '',
      technicians: techVal.map(String),
      is_completed: !!pkg.is_completed,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const validateForm = () => {
    const errs = {};
    if (!selectedClient && formMode === 'add') errs.client = 'Client required';
    if (!formData.name.trim()) errs.name = 'Package name required';
    if (!formData.price || isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0) errs.price = 'Valid price required';
    // Camp and Technicians are optional per latest requirement — do NOT enforce
    if (!formData.services || formData.services.length === 0) errs.services = 'Select at least one service';
    if (!formData.start_date) errs.start_date = 'Start date required';
    if (!formData.end_date) errs.end_date = 'End date required';
    if (formData.start_date && formData.end_date && new Date(formData.end_date) < new Date(formData.start_date)) errs.end_date = 'End date cannot be before start date';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      addToast('Please fix validation errors', 'error');
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        client: selectedClient ? selectedClient.id : (editingPackage?.client ? (typeof editingPackage.client === 'object' ? editingPackage.client.id : editingPackage.client) : null),
        name: formData.name.trim(),
        price: parseFloat(formData.price).toFixed(2),
        // Camp and Technicians are optional — send null / empty if not selected so backend doesn't reject
        camp: formData.camp ? parseInt(formData.camp, 10) : null,
        services: formData.services.map((id) => parseInt(id, 10)),
        report_language: formData.report_language,
        start_date: formData.start_date,
        end_date: formData.end_date,
        technicians: formData.technicians.length ? formData.technicians.map((id) => parseInt(id, 10)) : [],
        is_completed: !!formData.is_completed,
      };
      // Optional fields: omit camp if not selected so backend without allow_null still passes
      if (payload.camp === null) {
        delete payload.camp;
      }
      // technicians already sent as [] when empty — backend should accept empty list as optional

      const adminToken = localStorage.getItem('admin_access_token');
      const authHeader = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
      if (formMode === 'add') {
        await api.post('rural-health/packages/', payload, { headers: authHeader });
        addToast('Package created successfully', 'success');
      } else {
        const pkgId = editingPackage.id;
        // Try PATCH first, fallback to PUT
        try {
          await api.patch(`rural-health/packages/${pkgId}/`, payload, { headers: authHeader });
          addToast('Package updated successfully', 'success');
        } catch (patchErr) {
          if (patchErr.response?.status === 405 || patchErr.response?.status === 404) {
            await api.put(`rural-health/packages/${pkgId}/`, payload, { headers: authHeader });
            addToast('Package updated successfully', 'success');
          } else {
            throw patchErr;
          }
        }
      }
      setShowModal(false);
      // Refresh list
      if (selectedClient) fetchPackages(selectedClient);
    } catch (err) {
      const msg = err.response?.data?.detail || JSON.stringify(err.response?.data) || err.message || 'Failed to save package';
      addToast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (formErrors[key]) setFormErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const handleMultiToggle = (key, id) => {
    const idStr = String(id);
    setFormData((prev) => {
      const arr = prev[key] || [];
      const exists = arr.includes(idStr);
      const next = exists ? arr.filter((v) => v !== idStr) : [...arr, idStr];
      return { ...prev, [key]: next };
    });
    if (formErrors[key]) setFormErrors((prev) => ({ ...prev, [key]: '' }));
  };

  // Filter clients for display already computed

  return (
    <div className="space-y-6">
      <Toast toasts={toasts} removeToast={removeToast} />
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm">
              <Package className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Package Management</h1>
              <p className="text-sm text-gray-500">Manage packages per client</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchClients}
              disabled={loadingClients}
              className="p-2.5 rounded-xl border border-gray-200 bg-white text-gray-500 hover:text-blue-600 hover:border-blue-200 transition-colors shadow-sm disabled:opacity-50"
              title="Refresh clients"
            >
              <RefreshCw className={`h-4 w-4 ${loadingClients ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Client Search */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Client ID, Name, Email, Contact..."
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder-gray-400"
            />
          </div>
          <div className="text-sm text-gray-500 flex items-center px-3">
            {loadingClients ? 'Loading...' : `${filteredClients.length} client${filteredClients.length !== 1 ? 's' : ''} found`}
          </div>
        </div>
      </div>

      {/* Client List — packages expand inline just below the selected client row */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100">
        <div className="px-6 py-4 bg-gray-50 border-b flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-800">Clients</h2>
          <span className="text-xs text-gray-500">{clients.length} total clients (login_type=Client)</span>
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80">
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Client ID</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Client Name</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loadingClients ? (
                [...Array(5)].map((_, i) => <SkeletonRow key={i} cols={5} />)
              ) : clientError ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <AlertCircle className="h-8 w-8 text-rose-400" />
                      <p className="text-rose-600 text-sm">{clientError}</p>
                      <button onClick={fetchClients} className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">Retry</button>
                    </div>
                  </td>
                </tr>
              ) : filteredClients.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Building2 className="h-10 w-10 text-gray-300" />
                      <p className="text-gray-500 font-medium">No clients found</p>
                      <p className="text-gray-400 text-xs">{clientSearch ? 'Try adjusting search' : 'No Client type users available'}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredClients.map((client) => {
                  const isSelected = selectedClient?.id === client.id;
                  // inline package panel component for this row — rendered directly below the selected client
                  const InlinePanel = () => {
                    // Reuse the same panel JSX as defined above — inline expansion directly under client row
                    return (
                      <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden m-2">
                        <div className="px-4 py-3 bg-white border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div>
                            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                              <Package className="h-4 w-4 text-blue-600" />
                              Packages for {client.name}
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5">Client ID: {client.client_id} • DB ID: {client.id} • {client.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handleRefreshPackages}
                              disabled={loadingPackages}
                              className="p-2 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-blue-600 transition-colors disabled:opacity-50"
                              title="Refresh packages"
                            >
                              <RefreshCw className={`h-4 w-4 ${loadingPackages ? 'animate-spin' : ''}`} />
                            </button>
                            <button
                              onClick={openAddModal}
                              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                            >
                              <Plus className="h-4 w-4" />
                              Add Package
                            </button>
                          </div>
                        </div>
                        <div className="px-4 py-2 bg-slate-100 border-b flex justify-between items-center">
                          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Packages</span>
                          <span className="text-xs text-slate-500">{loadingPackages ? 'Loading...' : `${packages.length} package${packages.length !== 1 ? 's' : ''}`}</span>
                        </div>
                        <div className="overflow-x-auto bg-white">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-100 bg-gray-50/80">
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Package Name</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Price</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Camp</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Services</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Report Lang</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Start</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">End</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Technicians</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {loadingPackages ? (
                                [...Array(3)].map((_, i) => <SkeletonRow key={i} cols={10} />)
                              ) : packageError ? (
                                <tr>
                                  <td colSpan={10} className="py-8 text-center">
                                    <div className="flex flex-col items-center gap-2">
                                      <AlertCircle className="h-8 w-8 text-rose-400" />
                                      <p className="text-rose-600 text-sm max-w-md px-4">{packageError}</p>
                                      <button onClick={handleRefreshPackages} className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">Retry</button>
                                    </div>
                                  </td>
                                </tr>
                              ) : packages.length === 0 ? (
                                <tr>
                                  <td colSpan={10} className="py-8 text-center">
                                    <div className="flex flex-col items-center gap-3">
                                      <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center">
                                        <Package className="h-6 w-6 text-gray-400" />
                                      </div>
                                      <p className="text-gray-500 font-medium">No packages found for this client.</p>
                                      <button onClick={openAddModal} className="mt-2 flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                                        <Plus className="h-4 w-4" /> Add Package
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                packages.map((pkg) => (
                                  <tr key={pkg.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3 font-medium text-gray-800 max-w-[150px] truncate" title={pkg.name}>{pkg.name}</td>
                                    <td className="px-4 py-3 text-gray-700 font-mono text-xs">₹{pkg.price}</td>
                                    <td className="px-4 py-3 text-gray-600 text-xs max-w-[120px] truncate" title={getCampDisplay(pkg.camp)}>{getCampDisplay(pkg.camp)}</td>
                                    <td className="px-4 py-3 text-gray-600 text-xs max-w-[150px] truncate" title={getServiceNames(pkg.services)}>{getServiceNames(pkg.services)}</td>
                                    <td className="px-4 py-3 text-gray-600 text-xs">{pkg.report_language || 'English'}</td>
                                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{pkg.start_date ? pkg.start_date.substring(0,10) : '-'}</td>
                                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{pkg.end_date ? pkg.end_date.substring(0,10) : '-'}</td>
                                    <td className="px-4 py-3 text-gray-600 text-xs max-w-[120px] truncate" title={getTechnicianNames(pkg.technicians)}>{getTechnicianNames(pkg.technicians)}</td>
                                    <td className="px-4 py-3">
                                      <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold border ${pkg.is_completed ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                        {pkg.is_completed ? 'Completed' : 'Active'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <button onClick={() => openEditModal(pkg)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs font-medium transition-colors">
                                        <Edit className="h-3.5 w-3.5" /> Edit
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  };
                  return (
                    <React.Fragment key={client.id}>
                      <tr className={`hover:bg-blue-50/50 transition-colors ${isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}>
                        <td className="px-5 py-4 font-mono text-xs text-gray-700">{client.client_id}</td>
                        <td className="px-5 py-4 font-medium text-gray-800">{client.name}</td>
                        <td className="px-5 py-4 text-gray-600">{client.email}</td>
                        <td className="px-5 py-4 text-gray-600">{client.contact_number}</td>
                        <td className="px-5 py-4">
                          <button
                            onClick={() => handleSelectClient(client)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${isSelected ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-blue-600 hover:text-white'}`}
                          >
                            <Layers className="h-3.5 w-3.5" />
                            {isSelected ? 'Hide Packages' : 'View Packages'}
                          </button>
                        </td>
                      </tr>
                      {isSelected && (
                        <tr>
                          <td colSpan={5} className="p-3 bg-slate-50">
                            <InlinePanel />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile client cards — package panel expands inline just below the tapped client card */}
        <div className="md:hidden divide-y divide-gray-100">
          {loadingClients
            ? [...Array(3)].map((_, i) => (
                <div key={i} className="p-4 animate-pulse space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-2/3" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              ))
            : filteredClients.length === 0
              ? (
                <div className="py-10 text-center">
                  <Building2 className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No clients found</p>
                </div>
              )
              : filteredClients.map((client) => {
                  const isSelected = selectedClient?.id === client.id;
                  return (
                    <div key={client.id} className={`${isSelected ? 'bg-blue-50/50' : ''}`}>
                      <div className={`p-4 flex justify-between items-start gap-3 ${isSelected ? 'border-l-4 border-blue-500 bg-blue-50' : ''}`}>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-gray-800 text-sm truncate">{client.name}</p>
                          <p className="text-xs font-mono text-gray-500">{client.client_id}</p>
                          <p className="text-xs text-gray-500 mt-1 truncate">{client.email}</p>
                          <p className="text-xs text-gray-500">{client.contact_number}</p>
                        </div>
                        <button
                          onClick={() => handleSelectClient(client)}
                          className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium ${isSelected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                        >
                          {isSelected ? 'Hide' : 'View'}
                        </button>
                      </div>
                      {isSelected && (
                        <div className="px-3 pb-4">
                          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                            <div className="px-3 py-3 border-b flex items-center justify-between gap-2">
                              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                                <Package className="h-4 w-4 text-blue-600" /> Packages for {client.name}
                              </h3>
                              <button onClick={openAddModal} className="p-2 rounded-lg bg-blue-600 text-white">
                                <Plus className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="px-3 py-2 bg-slate-100 border-b flex justify-between items-center">
                              <span className="text-xs font-semibold text-slate-600 uppercase">Packages</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">{loadingPackages ? 'Loading...' : `${packages.length}`}</span>
                                <button onClick={handleRefreshPackages} disabled={loadingPackages} className="p-1 rounded border bg-white disabled:opacity-50">
                                  <RefreshCw className={`h-3 w-3 ${loadingPackages ? 'animate-spin' : ''}`} />
                                </button>
                              </div>
                            </div>
                            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                              {loadingPackages ? (
                                [...Array(2)].map((_, i) => (
                                  <div key={i} className="p-3 animate-pulse space-y-2">
                                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                                    <div className="h-3 bg-gray-100 rounded w-2/3" />
                                  </div>
                                ))
                              ) : packageError ? (
                                <div className="py-6 text-center px-3">
                                  <AlertCircle className="h-6 w-6 text-rose-400 mx-auto mb-1" />
                                  <p className="text-rose-600 text-xs">{packageError}</p>
                                </div>
                              ) : packages.length === 0 ? (
                                <div className="py-6 text-center">
                                  <Package className="h-6 w-6 text-gray-300 mx-auto mb-1" />
                                  <p className="text-gray-500 text-xs">No packages found for this client.</p>
                                  <button onClick={openAddModal} className="mt-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs">Add Package</button>
                                </div>
                              ) : packages.map((pkg) => (
                                <div key={pkg.id} className="p-3">
                                  <div className="flex justify-between items-start gap-2">
                                    <div className="min-w-0 flex-1">
                                      <p className="font-semibold text-gray-800 text-sm">{pkg.name}</p>
                                      <p className="text-xs text-gray-500 font-mono">₹{pkg.price} • {pkg.report_language || 'English'}</p>
                                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                                        <div><span className="text-gray-400">Camp:</span> <span className="text-gray-700">{getCampDisplay(pkg.camp)}</span></div>
                                        <div><span className="text-gray-400">Status:</span> <span className={`ml-1 px-1.5 py-0.5 rounded text-xs font-semibold ${pkg.is_completed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{pkg.is_completed ? 'Completed' : 'Active'}</span></div>
                                        <div className="col-span-2"><span className="text-gray-400">Services:</span> <span className="text-gray-700">{getServiceNames(pkg.services)}</span></div>
                                        <div className="col-span-2"><span className="text-gray-400">Technicians:</span> <span className="text-gray-700">{getTechnicianNames(pkg.technicians)}</span></div>
                                        <div><span className="text-gray-400">Start:</span> <span className="text-gray-700">{pkg.start_date ? pkg.start_date.substring(0,10) : '-'}</span></div>
                                        <div><span className="text-gray-400">End:</span> <span className="text-gray-700">{pkg.end_date ? pkg.end_date.substring(0,10) : '-'}</span></div>
                                      </div>
                                    </div>
                                    <button onClick={() => openEditModal(pkg)} className="shrink-0 p-2 rounded-lg bg-blue-50 text-blue-600">
                                      <Edit className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => !submitting && setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-[fadeUp_0.2s_ease_forwards]">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm">
                  <Package className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-800">{formMode === 'add' ? 'Add New Package' : 'Edit Package'}</h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {selectedClient ? `Client: ${selectedClient.name} (${selectedClient.client_id})` : editingPackage ? `Editing: ${editingPackage.name}` : ''}
                  </p>
                </div>
              </div>
              <button onClick={() => !submitting && setShowModal(false)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              {/* Client read-only */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  value={selectedClient ? `${selectedClient.name} (${selectedClient.client_id}) - ID:${selectedClient.id}` : (editingPackage?.client ? String(editingPackage.client) : '')}
                  disabled
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-600"
                />
                {formErrors.client && <p className="mt-1 text-xs text-rose-600">{formErrors.client}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Package Name <span className="text-rose-500">*</span></label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Basic Health Package"
                    className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.name ? 'border-rose-400 bg-rose-50' : 'border-gray-200 bg-slate-50'}`}
                  />
                  {formErrors.name && <p className="mt-1 text-xs text-rose-600">{formErrors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price <span className="text-rose-500">*</span></label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      placeholder="800.00"
                      className={`w-full pl-8 pr-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.price ? 'border-rose-400 bg-rose-50' : 'border-gray-200 bg-slate-50'}`}
                    />
                  </div>
                  {formErrors.price && <p className="mt-1 text-xs text-rose-600">{formErrors.price}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Camp <span className="text-gray-400 font-normal">(optional)</span></label>
                <select
                  value={formData.camp}
                  onChange={(e) => handleInputChange('camp', e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Camp (optional)</option>
                  {camps.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.location || c.name || `Camp ${c.id}`} {c.start_date ? `(${c.start_date.substring(0,10)})` : ''} - {c.client_name || c.client}
                    </option>
                  ))}
                </select>
                {loadingMeta && <p className="text-xs text-gray-400 mt-1">Loading camps...</p>}
                {formErrors.camp && <p className="mt-1 text-xs text-rose-600">{formErrors.camp}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Services <span className="text-rose-500">*</span></label>
                <div className={`rounded-xl border p-3 max-h-32 overflow-y-auto bg-slate-50 ${formErrors.services ? 'border-rose-400 bg-rose-50' : 'border-gray-200'}`}>
                  {services.length === 0 ? (
                    <p className="text-xs text-gray-400">{loadingMeta ? 'Loading services...' : 'No services available'}</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {services.map((s) => {
                        const checked = formData.services.includes(String(s.id));
                        return (
                          <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => handleMultiToggle('services', s.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-gray-700">{s.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                {formErrors.services && <p className="mt-1 text-xs text-rose-600">{formErrors.services}</p>}
                <p className="text-xs text-gray-400 mt-1">Selected: {formData.services.length}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Technicians <span className="text-gray-400 font-normal">(optional)</span></label>
                <div className="rounded-xl border border-gray-200 p-3 max-h-32 overflow-y-auto bg-slate-50">
                  {technicians.length === 0 ? (
                    <p className="text-xs text-gray-400">{loadingMeta ? 'Loading technicians...' : 'No technicians available'}</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {technicians.map((t) => {
                        const checked = formData.technicians.includes(String(t.id));
                        const name = t.name || t.user?.name || `Tech ${t.id}`;
                        return (
                          <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => handleMultiToggle('technicians', t.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-gray-700 truncate" title={name}>{name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                {formErrors.technicians && <p className="mt-1 text-xs text-rose-600">{formErrors.technicians}</p>}
                <p className="text-xs text-gray-400 mt-1">Selected: {formData.technicians.length}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Report Language</label>
                  <select
                    value={formData.report_language}
                    onChange={(e) => handleInputChange('report_language', e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {REPORT_LANGUAGE_OPTIONS.map((lang) => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_completed}
                      onChange={(e) => handleInputChange('is_completed', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Is Completed</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date <span className="text-rose-500">*</span></label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleInputChange('start_date', e.target.value)}
                    className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.start_date ? 'border-rose-400 bg-rose-50' : 'border-gray-200 bg-slate-50'}`}
                  />
                  {formErrors.start_date && <p className="mt-1 text-xs text-rose-600">{formErrors.start_date}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date <span className="text-rose-500">*</span></label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => handleInputChange('end_date', e.target.value)}
                    className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.end_date ? 'border-rose-400 bg-rose-50' : 'border-gray-200 bg-slate-50'}`}
                  />
                  {formErrors.end_date && <p className="mt-1 text-xs text-rose-600">{formErrors.end_date}</p>}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                  className="px-5 py-2 text-sm font-medium rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 text-sm font-medium rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-60 flex items-center gap-2"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {formMode === 'add' ? 'Create Package' : 'Update Package'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`@keyframes fadeUp { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
};

export default PackageManagement;
